import { useState, useEffect } from 'react';
import { novedadService } from '../services/novedadService';
import { TipoNovedad } from '../types/novedad';
import { PlusIcon } from '@heroicons/react/24/outline';

export const GestionTiposNovedadComponent = () => {
  const [tipos, setTipos] = useState<TipoNovedad[]>([]);
  const [formData, setFormData] = useState({ nombre: '', descripcion: '' });
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  useEffect(() => {
    loadTipos();
  }, []);

  const loadTipos = async () => {
    try {
      const tiposNovedad = await novedadService.getTiposNovedad();
      setTipos(tiposNovedad);
    } catch (error) {
      console.error('Error al cargar tipos de novedad:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nombre.trim()) return;

    setLoading(true);
    try {
      await novedadService.createTipoNovedad({
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim() || undefined
      });
      setFormData({ nombre: '', descripcion: '' });
      loadTipos();
    } catch (error) {
      console.error('Error al crear tipo de novedad:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleToggleActivo = async (tipo: TipoNovedad) => {
    setLoadingId(tipo.id);
    try {
      await novedadService.toggleActivo(tipo.id);
      loadTipos();
    } catch (error) {
      console.error('Error al toggle activo:', error);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Formulario de creación */}
      <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
          Crear Nuevo Tipo de Novedad
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="nombre" className="block text-sm font-medium text-gray-700">
              Nombre
            </label>
            <input
              type="text"
              name="nombre"
              id="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Ingrese el nombre del tipo de novedad"
            />
          </div>

          <div>
            <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700">
              Descripción
            </label>
            <textarea
              name="descripcion"
              id="descripcion"
              rows={3}
              value={formData.descripcion}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              placeholder="Describa el tipo de novedad"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <span className="inline-flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creando...
                </span>
              ) : (
                <>
                  <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                  Crear Tipo
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Lista de tipos existentes */}
      <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
          Tipos de Novedad Existentes
        </h3>
        <div className="space-y-4">
          {tipos.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No hay tipos de novedad registrados
            </p>
          ) : (
            <div className="divide-y divide-gray-200">
              {tipos.map((tipo) => (
                <div key={tipo.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <h4 className="text-sm font-medium text-gray-900">{tipo.nombre}</h4>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            tipo.activo
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {tipo.activo ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">{tipo.descripcion}</p>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <button
                        onClick={() => handleToggleActivo(tipo)}
                        disabled={loadingId === tipo.id}
                        className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-md ${
                          tipo.activo
                            ? 'border-red-300 text-red-700 hover:bg-red-50'
                            : 'border-green-300 text-green-700 hover:bg-green-50'
                        } disabled:opacity-50`}
                      >
                        {loadingId === tipo.id ? (
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : tipo.activo ? (
                          'Desactivar'
                        ) : (
                          'Activar'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
